import dayjs from 'dayjs';
import Button from '@liquidweb-dev/undercurrent/Button';
import Tab from '@liquidweb-dev/undercurrent/Tab';
import Tabs from '@liquidweb-dev/undercurrent/Tabs';
import { fromApiBoolean } from '@liquidweb-dev/club-sauce';
import FullScreenError from 'components/legacy/FullScreenError';
import Actions from 'components/HostingDetailsActions';
import Attributes from 'components/HostingDetailsAttributes';
import BackupsAlert from 'components/BackupsAlert';
import Description from 'components/HostingDetailsDescription';
import PageLayout, { PageLayoutInsetContent } from 'components/PageLayout';
import TabBar from 'components/TabBar';
import { IntroTarget, introTargets } from 'components/introjs';
import CancellationAlert from 'components/CancellationAlert';
import hostingDetailsAlsowith from 'pages/servers/details/hostingDetailsAlsowith';
import useReverseDns from 'modules/dns/useReverseDns';
import useAssetDetails from 'modules/queries/asset/useDetails';
import useMonitoringBandwidthStats from 'modules/queries/monitoring/bandwidth/useStats';
import { usePollNetworkIpListPublic } from 'modules/queries/network/ip/useListPublic';
import NotFound from 'pages/NotFound';
import React, { useState } from 'react';
import {
	Redirect,
	Link as RouterLink,
	useLocation,
	useParams,
} from 'react-router-dom';
import { isServerTypeWithHelpDocs } from 'utility/constants/serverTypeWithHelpDoc';
import { getErrorStringHelper } from 'utility/redux/selectorHelperFunctions/apiModules';
import useDocumentTitle from 'utility/hooks/useDocumentTitle';
import useTriggerServerViewEvent from 'utility/hooks/useTriggerServerViewEvent';
import DrawerDocsDetails from '../DrawerDocsDetails/DrawerDocsDetails';

/**
 * A layout for hosting details pages.
 */
const HostingDetailsLayout = ({ children }: { children: React.ReactNode }) => {
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const { uniqId } = useParams<{ uniqId: string }>();
	const { pathname } = useLocation();
	const {
		data: assetDetailsData,
		isLoading: assetDetailsIsLoading,
		isError: assetDetailsIsError,
		error: assetDetailsError,
	} = useAssetDetails(
		{
			uniq_id: uniqId,
			alsowith: hostingDetailsAlsowith,
		},
		{
			refetchInterval: 30000,
		},
	);
	const serverType = assetDetailsData?.type?.toLocaleLowerCase() || '';
	const { data: publicIps } = usePollNetworkIpListPublic({
		uniq_id: uniqId,
		page_size: 9999,
		page_num: 1,
		expand_ips: 1,
	});

	const { isError: isBandwidthStatsError } = useMonitoringBandwidthStats({
		uniq_id: uniqId,
	});

	const { assignableIps, isLoading: isReverseDnsLoading } = useReverseDns({
		uniqId,
	});

	const hostingDocumentTitle =
		assetDetailsData?.custom_name || assetDetailsData?.domain || '';
	useDocumentTitle(hostingDocumentTitle || 'Server Details');
	useTriggerServerViewEvent({ uniqId });

	if (assetDetailsIsError && assetDetailsError.statusCode === 404) {
		return <NotFound />;
	}

	if (assetDetailsIsError) {
		return (
			<FullScreenError content={getErrorStringHelper(assetDetailsIsError)} />
		);
	}

	const {
		canBackups,
		canConsole,
		canImage,
		capabilities,
		custom_name: customName,
		custom_description: customDescription,
		disableConsole,
		domain,
		isBeyondHosting,
		isNutanixPrivateCloud,
		isPrivateParent,
		isVmware,
		zone,
		isBareMetal,
		displayType,
	} = assetDetailsData || {};
	const { serverStatsCloud, serverStatsLocal } = capabilities || {};
	const canServerStatsCloud = fromApiBoolean(serverStatsCloud);
	const canServerStatsLocal = fromApiBoolean(serverStatsLocal);
	if (isBeyondHosting) {
		return <Redirect to={`/servers/${uniqId}/beyond-hosting`} />;
	}

	if (isVmware) {
		return <Redirect to={`/servers/${uniqId}/vmware-vdc`} />;
	}

	if (isNutanixPrivateCloud) {
		return <Redirect to={`/servers/${uniqId}/nutanix`} />;
	}

	if (isPrivateParent) {
		return <Redirect to={`/servers/${uniqId}/private-parent`} />;
	}

	const canMonitoring =
		canServerStatsLocal || canServerStatsCloud || !isBareMetal;

	const canNetworking =
		(zone?.id && fromApiBoolean(capabilities?.autoPrivateNetworking)) ||
		!isBandwidthStatsError ||
		capabilities?.provFirewall ||
		capabilities?.nocworxFirewall ||
		(publicIps && publicIps?.items.length > 0) ||
		Boolean(assignableIps?.length);

	const title = customName || domain || '';

	// the tabs correspond to the portion of the path after the server uniqId
	const tabsValue = pathname.split('/')[3] || 'overview';
	const cancellationDate =
		assetDetailsData?.scheduledCancellationDate?.DateTime;
	const isCancellationScheduled = cancellationDate
		? dayjs(cancellationDate).isSameOrAfter(dayjs(), 'day')
		: false;
	const nextRenewalDate = dayjs(
		assetDetailsData?.nextInvoiceDate?.DateTime,
	).format('MMMM D, YYYY');
	return (
		<>
			{isCancellationScheduled && (
				<CancellationAlert
					cancellationDate={cancellationDate}
					uniqId={uniqId}
					nextRenewalDate={nextRenewalDate}
					title={title}
				/>
			)}
			<PageLayout
				title={title}
				attributesSlot={<Attributes />}
				actionsSlot={<Actions />}
				alert={<BackupsAlert uniqId={uniqId} />}
				isLoading={assetDetailsIsLoading}
				description={
					customDescription && (
						<Description serverDescription={customDescription} />
					)
				}
				variant="dynamic"
			>
				<TabBar
					sx={{ marginBottom: 600 }}
					isLoading={assetDetailsIsLoading || isReverseDnsLoading}
				>
					<Tabs value={tabsValue} variant="scrollable">
						<Tab
							component={RouterLink}
							value="overview"
							to={`/servers/${uniqId}`}
							label="Overview"
						/>
						{canMonitoring && (
							<Tab
								component={RouterLink}
								value="monitoring"
								to={`/servers/${uniqId}/monitoring`}
								label="Monitoring"
							/>
						)}
						{canNetworking && (
							<Tab
								component={RouterLink}
								value="networking"
								to={`/servers/${uniqId}/networking`}
								label="Networking"
							/>
						)}
						{canBackups && (
							<Tab
								component={RouterLink}
								value="backups"
								to={`/servers/${uniqId}/backups`}
								label="Backups"
							/>
						)}
						{canImage && (
							<Tab
								component={RouterLink}
								value="images"
								to={`/servers/${uniqId}/images`}
								label="Images"
							/>
						)}
						<Tab
							component={RouterLink}
							value="activity"
							to={`/servers/${uniqId}/activity`}
							label="Activity"
						/>
						{canConsole && !disableConsole && (
							<Tab
								component={RouterLink}
								value="console"
								to={`/servers/${uniqId}/console`}
								label="Console"
							/>
						)}
						<Tab
							component={RouterLink}
							value="settings"
							to={`/servers/${uniqId}/settings`}
							label={
								<IntroTarget introId={introTargets.serverInteriorSettingsTab}>
									<span>Settings</span>
								</IntroTarget>
							}
						/>
					</Tabs>
					{isServerTypeWithHelpDocs(serverType) && (
						<Button
							variant="ghost"
							onClick={() => setIsDrawerOpen(true)}
							sx={({ size, vars }) => ({
								color: vars.palette.uc.text.main,
								height: size.lg,
								paddingY: 100,
								paddingX: 150,
							})}
						>
							{`${displayType} Docs`}
						</Button>
					)}
				</TabBar>
				<PageLayoutInsetContent sx={{ flexGrow: 1 }}>
					{children}
				</PageLayoutInsetContent>
				<DrawerDocsDetails
					uniqId={uniqId}
					open={isDrawerOpen}
					onClose={() => setIsDrawerOpen(false)}
				/>
			</PageLayout>
		</>
	);
};

export default HostingDetailsLayout;
